import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface PermVisibilityToggleProps {
  /** Libellé lisible affiché au-dessus de l'interrupteur. */
  label: string;
  /** Clé technique affichée en petit sous l'interrupteur (facultatif). */
  itemKey?: string;
  visible: boolean;
  onChange: (visible: boolean) => void;
}

const OPTIONS = [
  { value: true, icon: Eye, labelKey: "droits.etat.visible" },
  { value: false, icon: EyeOff, labelKey: "droits.etat.cache" },
] as const;

/**
 * Interrupteur à 2 états (Visible/Caché) pour un onglet ou un bloc entier, en
 * mode réglage des droits.
 *
 * Contrairement à `PermStateCell` (3 états, un par champ), pas de notion
 * "Éditable" — ça n'a pas de sens pour une structure entière — et pas de
 * register/unregister : la liste des onglets/blocs masquables est fixe et
 * connue d'avance (FICHE_TAB_IDS, BienDetailHideableSection), pas découverte
 * par le rendu comme les champs individuels.
 */
export function PermVisibilityToggle({ label, itemKey, visible, onChange }: PermVisibilityToggleProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col min-w-0">
      <span className="text-xs font-medium tracking-wider uppercase text-text-muted px-3 mb-1 truncate" title={label}>
        {label}
      </span>
      <div
        role="radiogroup"
        aria-label={label}
        className="flex items-stretch gap-1 px-1 py-1 bg-surface border border-solid border-border rounded-md"
      >
        {OPTIONS.map(({ value, icon: Icon, labelKey }) => {
          const active = visible === value;
          return (
            <button
              key={String(value)}
              type="button"
              role="radio"
              aria-checked={active}
              title={t(labelKey)}
              onClick={() => onChange(value)}
              className={cn(
                "flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-[11px] font-medium",
                "cursor-pointer outline-none shadow-none transition-colors min-w-0",
                active
                  ? "bg-accent text-white"
                  : "text-text-muted hover:bg-surface-2 hover:text-text-secondary",
              )}
            >
              <Icon size={12} className="shrink-0" />
              <span className="truncate">{t(labelKey)}</span>
            </button>
          );
        })}
      </div>
      {itemKey && (
        <span className="text-[10px] text-text-muted/70 px-3 mt-0.5 truncate font-mono" title={itemKey}>
          {itemKey}
        </span>
      )}
    </div>
  );
}
